import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { ExternalLink, Star, Trophy } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { GradeBadge } from '../../../components/climb-log/GradeBadge';
import { memberAppClimbUrl } from '../../member-app-url';
import { BoardCardActions } from './BoardCardActions';
import { ClimbThumbnail } from './ClimbThumbnail';
import { ageStatus, daysSince, type AgeStatus } from './board-utils';
import type { RouteSettingBoardCard } from '../../../types/route-setting-board';

interface BoardCardProps {
  card: RouteSettingBoardCard;
  /** The gym the board belongs to, for the link out to the climb's page. */
  gymSlug?: string | null;
  /**
   * The board's average age in days — what this card's own age is read against
   * to decide whether it is fresh, due or overdue. Omitted in the drag overlay,
   * where the card is a copy following the pointer.
   */
  averageAgeDays?: number | null;
  /** Opens the full photo with every hold drawn. */
  onImageClick?: (card: RouteSettingBoardCard) => void;
  /** Makes a member-reported climb official. Omitted in the drag overlay. */
  onConfirm?: (card: RouteSettingBoardCard) => void;
  /** Takes the climb off the wall. Omitted in the drag overlay. */
  onDelete?: (card: RouteSettingBoardCard) => void;
  /**
   * False when the columns are not zones: a card dragged onto a grade or a
   * setter has nothing to be moved into, so it stays put and reads as static.
   */
  draggable?: boolean;
  /** Rendered inside the drag overlay, where dnd-kit must not bind it twice. */
  overlay?: boolean;
}

/** Day/month of the set date — the year is never in question on a wall. */
function formatSetDate(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(date);
}

/**
 * How long the climb has been up, at a glance: red has waited well past the
 * board's average, yellow is past it, green went up recently. Carried by the
 * card's edge and a wash of colour rather than by the border alone, so it still
 * reads on a crowded column.
 */
const AGE_STYLES: Record<AgeStatus, string> = {
  overdue:
    'border-l-4 border-l-red-500 bg-red-50 dark:border-l-red-500 dark:bg-red-950/40',
  due: 'border-l-4 border-l-amber-500 bg-amber-50 dark:border-l-amber-500 dark:bg-amber-950/40',
  fresh:
    'border-l-4 border-l-emerald-500 bg-emerald-50 dark:border-l-emerald-500 dark:bg-emerald-950/40',
  normal: '',
};

export function BoardCard({
  card,
  gymSlug,
  averageAgeDays = null,
  onImageClick,
  onConfirm,
  onDelete,
  draggable = true,
  overlay = false,
}: BoardCardProps) {
  const { t, i18n } = useTranslation();
  const inert = overlay || !draggable;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
    disabled: inert,
  });

  const setter = card.route_setter;
  const setterName = setter?.name ?? null;
  const swatches = card.hold_color_hex ?? [];
  const age = daysSince(card.created_at);
  const status = ageStatus(age, averageAgeDays);

  const showLink = Boolean(gymSlug) && !overlay;
  // The drag overlay is handed no callbacks, so it gets no footer to draw.
  const showActions = showLink || Boolean(onConfirm) || Boolean(onDelete);

  return (
    <div
      ref={inert ? undefined : setNodeRef}
      style={inert ? undefined : { transform: CSS.Translate.toString(transform) }}
      data-testid="route-setting-card"
      data-age-status={status}
      className={[
        'flex flex-col gap-1.5 rounded-lg border bg-card p-2 shadow-sm',
        AGE_STYLES[status],
        overlay ? 'cursor-grabbing shadow-lg' : '',
        !overlay && draggable ? 'cursor-grab touch-none' : '',
        // The original stays in place as a ghost while the overlay follows the
        // pointer, so the column does not visibly reflow mid-drag.
        isDragging ? 'opacity-40' : '',
      ].join(' ')}
      {...(inert ? {} : attributes)}
      {...(inert ? {} : listeners)}
    >
      <div className="flex gap-2">
        <ClimbThumbnail
          imageUrl={card.image_medium_url}
          holds={card.holds}
          line={card.line}
          alt={card.name}
          label={t('gymAdmin.routeSettingBoard.openImage')}
          onClick={() => onImageClick?.(card)}
        />

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <h4 className="min-w-0 flex-1 truncate text-sm font-medium">{card.name}</h4>
            {setter && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Avatar className="size-6 shrink-0">
                    <AvatarImage src={setter.avatar_small_url ?? undefined} alt="" />
                    <AvatarFallback className="text-[10px]">
                      {(setterName ?? '?').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent>{setterName}</TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* No "pending" badge here: on a card this narrow it cost a line of
              its own, and the confirm button below says the same thing. The
              photo dialog, which has the room, still carries it. */}
          <div className="flex flex-wrap items-center gap-1.5">
            <GradeBadge
              difficulty={card.difficulty}
              averageGradeOpinion={card.average_grade_opinion}
              size="sm"
            />
            {!card.difficulty && (
              <span className="text-xs text-muted-foreground">{t('climbs.noGrade')}</span>
            )}

            {/* Hold colour: the gym's own name for it, with the scanned swatches
                beside it when a scan resolved them. */}
            {card.hold_color && (
              <span
                className="flex items-center gap-1 rounded border bg-muted px-1.5 py-0.5 text-xs"
                data-testid="card-hold-color"
              >
                {swatches.slice(0, 2).map((hex) => (
                  <span
                    key={hex}
                    data-testid="card-hold-swatch"
                    className="size-2.5 rounded-full border border-border"
                    style={{ backgroundColor: hex }}
                  />
                ))}
                {card.hold_color}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {/* The colour is never the only way to read the age: the date says how
                long ago, and its tooltip says what that means for this board. */}
            <span
              title={
                status === 'normal'
                  ? t('gymAdmin.routeSettingBoard.setOn')
                  : t(`gymAdmin.routeSettingBoard.age.${status}`)
              }
            >
              {formatSetDate(card.created_at, i18n.language)}
            </span>
            <span className="flex items-center gap-1" title={t('gymAdmin.routeSettingBoard.sends')}>
              <Trophy className="size-3" aria-hidden />
              {card.sends_count}
            </span>
            {card.average_rating != null && card.average_rating > 0 && (
              <span
                className="flex items-center gap-1 text-yellow-500"
                title={t('gymAdmin.routeSettingBoard.averageStars')}
              >
                <Star className="size-3 fill-current" aria-hidden />
                {card.average_rating.toFixed(1)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* The actions get a row of their own across the whole card. Sharing the
          line with the date left them the ~148px beside the thumbnail, which
          two labelled buttons and a link overflow. */}
      {showActions && (
        <div
          className="flex flex-wrap items-center justify-end gap-1"
          data-testid="card-actions"
        >
          {showLink && (
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={memberAppClimbUrl(gymSlug ?? '', card.slug)}
                  target="_blank"
                  rel="noopener noreferrer"
                  // Same reason as the thumbnail: the card is the drag handle.
                  onPointerDown={(event) => event.stopPropagation()}
                  aria-label={t('gymAdmin.routeSettingBoard.openClimb')}
                  data-testid="card-open-climb"
                  className="inline-flex size-5 items-center justify-center rounded hover:bg-accent hover:text-foreground"
                >
                  <ExternalLink className="size-3" aria-hidden />
                </a>
              </TooltipTrigger>
              <TooltipContent>{t('gymAdmin.routeSettingBoard.openClimb')}</TooltipContent>
            </Tooltip>
          )}

          <BoardCardActions card={card} onConfirm={onConfirm} onDelete={onDelete} />
        </div>
      )}
    </div>
  );
}
